import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Globe, MapPin, Map, Building2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const HERO_IMG = "https://images.unsplash.com/photo-1511192560771-b8b7da019cf2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export const Hero = ({ countries = [] }) => {
  const [countrySlug, setCountrySlug] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const navigate = useNavigate();

  const selected = countries.find((c) => c.slug === countrySlug);
  const states = selected?.states || [];
  const cities = states.find((s) => s.slug === state)?.cities || [];

  const explore = () => {
    if (countrySlug) navigate(`/places/${countrySlug}`);
    else navigate("/explore");
  };

  return (
    <section className="relative overflow-hidden bg-white border-b border-slate-100">
      <div className="absolute inset-y-0 right-0 w-full lg:w-[60%]">
        <img src={HERO_IMG} alt="Instruments and music cultures of the world" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 lg:via-white/40 to-white/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
      </div>

      <p className="hidden lg:block absolute top-8 left-[46%] font-script text-2xl text-purple-700 -rotate-6 z-10 drop-shadow-sm">Different Cultures<br />Same Beat</p>
      <div className="hidden lg:block absolute top-8 right-8 z-10 text-right bg-white/60 backdrop-blur-sm rounded-2xl px-3 py-2">
        <p className="text-[11px] font-black tracking-[0.35em] text-purple-700 uppercase leading-relaxed">Music<br />Knows<br />No Borders</p>
        <span className="inline-block w-10 h-0.5 bg-pink-500 mt-1" />
      </div>
      <p className="hidden lg:block absolute bottom-24 right-10 font-script text-2xl text-slate-800 rotate-3 z-10 bg-white/50 backdrop-blur-sm rounded-2xl px-3 py-1">More Music,<br />A Kinder World</p>

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-6">
        <div className="max-w-xl fade-up">
          <p className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500 mb-4">Music connects us all</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] text-slate-900">
            The World Has a Sound.
            <br />
            <span className="gradient-text">Discover It.</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            Explore music by country, culture, language, genre, instrument and era.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button data-testid="hero-explore-btn" onClick={() => navigate("/genres")} className="gradient-btn text-white font-bold px-6 py-3 rounded-xl inline-flex items-center gap-2">
              Explore Music <ArrowRight className="w-4 h-4" />
            </button>
            <button data-testid="hero-browse-world-btn" onClick={() => navigate("/explore")} className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl border-2 border-purple-300 bg-white/80 backdrop-blur text-purple-700 hover:bg-purple-50 transition-colors">
              <Globe className="w-4 h-4" /> Browse the World
            </button>
          </div>
        </div>

        <div className="relative mt-10 bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/60 p-4 flex flex-col xl:flex-row xl:items-center gap-4" data-testid="geo-finder-bar">
          <div className="flex items-center gap-3 xl:w-80 shrink-0">
            <div className="w-10 h-10 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-sm text-slate-900">Find Music Near You <span className="text-slate-400 font-medium">(or Far Away)</span></p>
              <p className="text-xs text-slate-500">Discover artists, events and sounds from every corner of the world.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
            <Select value={countrySlug} onValueChange={(v) => { setCountrySlug(v); setState(""); setCity(""); }}>
              <SelectTrigger data-testid="geo-country-select" className="rounded-xl h-11">
                <span className="flex items-center gap-2 truncate"><Globe className="w-4 h-4 text-slate-400 shrink-0" /><SelectValue placeholder="Select Country" /></span>
              </SelectTrigger>
              <SelectContent>
                {countries.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.flag} {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={state} onValueChange={(v) => { setState(v); setCity(""); }} disabled={!states.length}>
              <SelectTrigger data-testid="geo-state-select" className="rounded-xl h-11">
                <span className="flex items-center gap-2 truncate"><Map className="w-4 h-4 text-slate-400 shrink-0" /><SelectValue placeholder="State / Province" /></span>
              </SelectTrigger>
              <SelectContent>
                {states.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={city} onValueChange={setCity} disabled={!cities.length}>
              <SelectTrigger data-testid="geo-city-select" className="rounded-xl h-11">
                <span className="flex items-center gap-2 truncate"><Building2 className="w-4 h-4 text-slate-400 shrink-0" /><SelectValue placeholder="City" /></span>
              </SelectTrigger>
              <SelectContent>
                {cities.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button data-testid="geo-explore-btn" onClick={explore} className="gradient-btn text-white font-bold px-8 py-3 rounded-xl inline-flex items-center justify-center gap-2 shrink-0">
            Explore <ArrowRight className="w-4 h-4" />
          </button>
          <p className="hidden 2xl:block font-script text-xl text-pink-500 -rotate-2 shrink-0 pr-2">Music Lives Everywhere</p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
